/** @jsxImportSource @emotion/react */
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import CampaignCard from "./CampaignCard";
import CampaignsCarousel from "./CampaignsCarousel";
import t from "../../../theme";

const EMPTY_CAMPAIGNS = [];

const styles = {
  section: {
    padding: 0,
  },
  compactSection: {
    padding: "10px 14px 6px",
    "@media (max-width: 640px)": {
      padding: "10px 10px 6px",
    },
  },
  shell: {
    borderRadius: 20,
    padding: 20,
    background: t.glass.secondary,
    backdropFilter: t.blur.heavy,
    WebkitBackdropFilter: t.blur.heavy,
    border: `1px solid ${t.green.border}`,
    boxShadow: "0 10px 28px rgba(46, 125, 50, 0.08), inset 0 1px 0 rgba(255,255,255,0.45)",
    fontFamily: t.font.family,
  },
  bareShell: {
    padding: 0,
    border: 0,
    background: "transparent",
    backdropFilter: "none",
    WebkitBackdropFilter: "none",
    boxShadow: "none",
  },
  compactShell: {
    borderRadius: 16,
    padding: 12,
    "@media (max-width: 640px)": {
      padding: 0,
      border: 0,
      background: "transparent",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 16,
  },
  compactHeader: {
    marginBottom: 9,
    alignItems: "baseline",
    justifyContent: "space-between",
    "@media (max-width: 640px)": {
      alignItems: "flex-start",
      flexDirection: "column",
      gap: 8,
    },
  },
  compactHeaderCopy: {
    display: "flex",
    alignItems: "baseline",
    gap: 10,
    flexWrap: "wrap",
  },
  title: {
    margin: 0,
    color: "#101828",
    fontSize: "1.3rem",
    lineHeight: 1.2,
    fontWeight: 700,
  },
  compactTitle: {
    fontSize: "1.05rem",
  },
  subtitle: {
    margin: "4px 0 0",
    color: "#667085",
    fontSize: "0.78rem",
  },
  compactSubtitle: {
    fontSize: "0.72rem",
    margin: 0,
    fontWeight: 600,
    color: "#008060",
  },
  compactLinks: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginLeft: "auto",
    "@media (max-width: 640px)": {
      marginLeft: 0,
      width: "100%",
      justifyContent: "flex-start",
      flexWrap: "wrap",
    },
  },
  compactLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    border: 0,
    background: "transparent",
    color: "#666",
    cursor: "pointer",
    fontFamily: t.font.family,
    fontSize: "0.84rem",
    fontWeight: 600,
    padding: 0,
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
    letterSpacing: "0.15px",
    "&:hover": {
      color: "#2e7d32",
    },
    "&:focus-visible": {
      outline: "2px solid rgba(46, 125, 50, 0.25)",
      outlineOffset: 2,
    },
    "& svg": {
      fontSize: "1rem",
    },
  },
  compactLinkSeparator: {
    width: 1,
    height: 16,
    background: "rgba(46, 125, 50, 0.22)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 16,
    padding: "4px 0",
    alignItems: "stretch",
    "@media (max-width: 1024px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    "@media (max-width: 640px)": {
      gridTemplateColumns: "1fr",
      gap: 16,
    },
  },
  listingSkeleton: {
    width: "100%",
    aspectRatio: "3 / 2",
    borderRadius: 14,
  },
  state: {
    borderRadius: 16,
    padding: 24,
    background: "#ffffff",
    border: "1px dashed #cbd5e1",
    color: "#667085",
    textAlign: "center",
    fontWeight: 700,
  },
  viewAll: {
    borderRadius: 999,
    textTransform: "none",
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
};

const CampaignsPanel = ({
  campaigns,
  count,
  isLoading,
  isError,
  onOpenCampaign,
  onViewAll,
  onViewBookings,
  compact = false,
  bare = false,
}) => {
  const campaignItems = campaigns || EMPTY_CAMPAIGNS;

  if (!isLoading && !isError && campaignItems.length === 0 && compact) return null;

  return (
    <section css={[styles.section, compact && styles.compactSection]}>
      <div css={[styles.shell, compact && styles.compactShell, bare && styles.bareShell]}>
        {(compact || onViewAll) && <div css={[styles.header, compact && styles.compactHeader]}>
          {compact && (
            <div css={styles.compactHeaderCopy}>
              <h2 css={[styles.title, styles.compactTitle]}>
                Ongoing Campaigns
              </h2>
              <p css={[styles.subtitle, styles.compactSubtitle]}>
                Claim your spot
              </p>
            </div>
          )}
          {compact && (
            <div css={styles.compactLinks}>
              <button
                type="button"
                css={styles.compactLink}
                onClick={onViewBookings}
              >
                <ConfirmationNumberOutlinedIcon />
                My Bookings
              </button>
              <span css={styles.compactLinkSeparator} />
              <button type="button" css={styles.compactLink} onClick={onViewAll}>
                <CampaignOutlinedIcon />
                All Campaigns
              </button>
            </div>
          )}
          {onViewAll && count > campaignItems.length && (
            <Button
              css={styles.viewAll}
              variant="outlined"
              onClick={onViewAll}
              style={{ display: compact ? "none" : undefined }}
            >
              View all
            </Button>
          )}
        </div>}

        {isError && <div css={styles.state}>Unable to load campaigns right now.</div>}

        {!isError && compact && (
          <CampaignsCarousel
            campaigns={campaignItems}
            isLoading={isLoading}
            onOpenCampaign={onOpenCampaign}
          />
        )}

        {!isError && !compact && isLoading && (
          <div css={styles.grid}>
            {[0, 1, 2, 3].map((item) => (
              <Skeleton key={item} variant="rounded" css={styles.listingSkeleton} />
            ))}
          </div>
        )}

        {!isError && !compact && !isLoading && campaignItems.length === 0 && (
          <div css={styles.state}>No ongoing campaigns are available.</div>
        )}

        {!isError && !compact && !isLoading && campaignItems.length > 0 && (
          <div css={styles.grid}>
            {campaignItems.map((campaign, index) => (
              <CampaignCard
                key={campaign.uuid || campaign.campaign_uuid || campaign.id || index}
                campaign={campaign}
                onOpen={onOpenCampaign}
                compact
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default CampaignsPanel;
